import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, SearchInput, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal, Tooltip
} from '../../components/UI';
import { usersAPI, callTargetAPI } from '../../services/api';
import { useOrg } from '../../contexts/OrgContext';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus, HiOutlineUsers, HiOutlinePhone,
  HiOutlineMail, HiOutlinePencil, HiOutlineTrash, HiOutlineDownload,
  HiOutlineCheck, HiOutlineX, HiOutlineChevronUp, HiOutlineChevronDown,
  HiOutlineTrendingUp, HiOutlineEye
} from 'react-icons/hi';
import { format } from 'date-fns';
import UserModal from './UserModal';
import SeatLimitBanner, { useSeatUsage } from '../../components/SeatLimitBanner';
import { formatLabel } from '../../utils/formatLabel';

// Build a compact list of page numbers + "..." ellipses for pagination.
// Always shows first and last page, current page ±1, with ellipses for gaps.
// e.g. page 5 of 20 -> [1, "...", 4, 5, 6, "...", 20]
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push('...');
    }
  }
  return result;
};

const pageSizeOptions = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];

const roleOptions = [
  { value: '', label: 'All Roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'telecaller', label: 'Telecaller' },
];

const UsersList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { checkLimit, usage, plan, refreshOrg } = useOrg();
  const userLimitInfo = checkLimit('users');
  const seatUsage = useSeatUsage();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [transferToUserId, setTransferToUserId] = useState('');

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState(''); // 'asc' | 'desc'

  // Assign Target modal state
  const [showAssignTargetModal, setShowAssignTargetModal] = useState(false);
  const [targetValue, setTargetValue] = useState('');
  const [assignTargetLoading, setAssignTargetLoading] = useState(false);

  // Fetch users with pagination and server-side search
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', page, pageSize, roleFilter, sortBy, sortOrder, search],
    queryFn: async () => {
      const params = { page, limit: pageSize };
      if (roleFilter) params.role = roleFilter;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;
      if (search) params.search = search;
      const response = await usersAPI.getAll(params);
      return response.data || {};
    },
  });

  const users = usersData?.data || [];
  const pagination = usersData?.pagination || {};

  // Delete mutation — carries the transfer target picked in the delete dialog
  const deleteMutation = useMutation({
    mutationFn: ({ id, transferToUserId }) =>
      usersAPI.delete(id, transferToUserId ? { transferToUserId } : undefined),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['users']);
      queryClient.invalidateQueries(['user-data-summary']);
      toast.success(response.data?.message || 'User deleted successfully');
      setShowDeleteModal(false);
      setUserToDelete(null);
      setTransferToUserId('');
      refreshOrg();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    },
  });

  // Bulk update status mutation
  const bulkUpdateStatusMutation = useMutation({
    mutationFn: ({ ids, isActive }) => usersAPI.bulkUpdate({ ids, isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success('User status updated successfully');
      setSelectedUsers([]);
      setShowBulkStatusModal(false);
      refreshOrg();
    },
    onError: (error) => {
      const responseData = error.response?.data;
      if (responseData?.code === 'LIMIT_EXCEEDED') {
        toast.error(responseData.message || 'User limit reached', { duration: 5000 });
        refreshOrg();
      } else {
        toast.error(responseData?.message || 'Failed to update user status');
      }
    },
  });

  // Reset page when filters change
  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  // Selection handlers
  const handleSelectUser = (id) => {
    setSelectedUsers(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length && users.length > 0) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u._id));
    }
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Cycle: asc → desc → clear
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        // desc → clear (no sort)
        setSortBy('');
        setSortOrder('');
      }
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return (
        <span className="inline-flex flex-col ml-0.5 opacity-30">
          <HiOutlineChevronUp className="w-3 h-3 -mb-1" />
          <HiOutlineChevronDown className="w-3 h-3" />
        </span>
      );
    }
    return sortOrder === 'asc' ? (
      <HiOutlineChevronUp className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    ) : (
      <HiOutlineChevronDown className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    );
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setTransferToUserId('');
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate({ id: userToDelete._id, transferToUserId });
    }
  };

  // What the user being deleted owns + who it can be transferred to.
  // Fetched while the delete dialog is open so it can show the transfer step.
  const deleteSummaryQuery = useQuery({
    queryKey: ['user-data-summary', userToDelete?._id],
    queryFn: async () => {
      const response = await usersAPI.getDataSummary(userToDelete._id);
      return response.data?.data || {};
    },
    enabled: !!userToDelete && showDeleteModal,
  });
  const deleteCounts = deleteSummaryQuery.data?.counts || {};
  const deleteCandidates = deleteSummaryQuery.data?.candidates || [];
  const hasDataToDelete = Object.values(deleteCounts).some((c) => c > 0);

  // Export users to CSV
  const handleExportUsers = () => {
    try {
      if (users.length === 0) {
        toast.error('No users to export');
        return;
      }

      // Create CSV headers
      const headers = ['Name', 'Email', 'Role', 'Primary Phone', 'All Phones', 'Department', 'Status', 'Created At'];

      // Create CSV rows
      const csvRows = [
        headers.join(','),
        ...users.map(user => {
          const phones = user.phones || [];
          const primaryPhone = phones.find(p => p.isDefault)?.number || phones[0]?.number || '';
          const allPhones = phones.map(p => `${p.number} (${p.label})`).join('; ');

          return [
            `"${user.firstName || ''} ${user.lastName || ''}"`.trim(),
            `"${user.email || ''}"`,
            `"${user.role || ''}"`,
            `"${primaryPhone}"`,
            `"${allPhones}"`,
            `"${user.department || ''}"`,
            `"${user.isActive !== false ? 'Active' : 'Inactive'}"`,
            `"${user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ''}"`
          ].join(',');
        })
      ];

      // Add summary section
      csvRows.unshift('');
      csvRows.unshift(`Total Users: ${pagination.total || users.length}`);
      csvRows.unshift(`Export Date: ${new Date().toLocaleString()}`);
      csvRows.unshift('');
      csvRows.unshift('=== USERS EXPORT ===');
      csvRows.unshift('');

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `users-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${users.length} users successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export users');
    }
  };

  // Helper function to get display phone
  const getDisplayPhone = (user) => {
    const phones = user.phones || [];
    if (phones.length === 0) return '-';
    const defaultPhone = phones.find(p => p.isDefault) || phones[0];
    return defaultPhone.number;
  };

  // Helper function to get all phones count
  const getPhonesCount = (user) => {
    return (user.phones || []).filter(p => p.number).length;
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin':
      default:
        return 'primary';
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Users</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Manage telecallers and staff
              {usage && (
                <span className="ml-2 text-gray-600 font-medium">
                  ({seatUsage.activeUsers ?? usage.users}{userLimitInfo.limit !== 'unlimited' ? `/${userLimitInfo.limit}` : ''} users{plan?.isPerUser ? ' · admin included free' : ''})
                </span>
              )}
            </p>
            {!userLimitInfo.allowed && (
              <p className="text-xs text-red-600 mt-1 font-medium">
                Telecaller limit reached. {plan?.isPerUser ? 'Add more seats or' : ''} Upgrade your plan to add more telecallers.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Tooltip text="Export users to CSV file">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineDownload className="w-4 h-4" />}
                onClick={handleExportUsers}
              >
                <span className="hidden sm:inline">Export</span>
              </Button>
            </Tooltip>
            <Tooltip text={userLimitInfo.allowed ? "Add a new user" : "User limit reached"}>
              <Button
                variant="primary"
                size="sm"
                icon={<HiOutlinePlus className="w-4 h-4" />}
                onClick={handleAddUser}
                disabled={!userLimitInfo.allowed}
              >
                <span className="hidden sm:inline">Add User</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Over-seat-limit warning (per-user plans) */}
        <SeatLimitBanner />

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1">
              <SearchInput
                value={search}
                onChange={handleSearchChange}
                placeholder="Search users..."
              />
            </div>
            <Select
              options={roleOptions}
              value={roleFilter}
              onChange={handleRoleFilterChange}
              className="sm:w-40"
            />
            <div className="flex items-center gap-1.5">
              <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Rows per page</span>
              <Select
                options={pageSizeOptions}
                value={String(pageSize)}
                onChange={handlePageSizeChange}
                className="sm:w-20"
              />
            </div>
          </div>
        </Card>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-primary-50 rounded-xl animate-fadeIn">
            <span className="text-xs sm:text-sm font-medium text-primary-700">
              {selectedUsers.length} selected
            </span>
            <Tooltip text="Activate selected users">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineCheck className="w-4 h-4" />}
                onClick={() => setShowBulkStatusModal(true)}
              >
                <span className="hidden sm:inline">Activate</span>
              </Button>
            </Tooltip>
            <Tooltip text="Deactivate selected users">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineX className="w-4 h-4" />}
                onClick={() => {
                  bulkUpdateStatusMutation.mutate({ ids: selectedUsers, isActive: false });
                }}
                disabled={bulkUpdateStatusMutation.isPending}
              >
                <span className="hidden sm:inline">Deactivate</span>
              </Button>
            </Tooltip>
            {selectedUsers.some(id => users.find(u => u._id === id && u.role === 'telecaller')) && (
              <Tooltip text="Set daily call target for selected telecallers">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<HiOutlineTrendingUp className="w-4 h-4" />}
                  onClick={() => {
                    setTargetValue('');
                    setShowAssignTargetModal(true);
                  }}
                >
                  <span className="hidden sm:inline">Assign Target</span>
                  <span className="sm:hidden">Target</span>
                </Button>
              </Tooltip>
            )}
            <Tooltip text="Clear selection">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearSelection}
              >
                Clear
              </Button>
            </Tooltip>
          </div>
        )}

        {/* Users List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : users.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineUsers className="w-8 h-8 text-gray-400" />}
              title="No users found"
              description={search || roleFilter ? "Try adjusting your search or filters" : "Add your first user to get started"}
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header>
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === users.length && users.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600"
                      />
                    </TableCell>
                    <TableCell header>Sr No.</TableCell>
                    <TableCell header>
                      <span
                        className="inline-flex items-center cursor-pointer select-none hover:text-primary-700 transition-colors"
                        onClick={() => handleSort('firstName')}
                      >
                        User <SortIcon field="firstName" />
                      </span>
                    </TableCell>
                    <TableCell header>
                      <span
                        className="inline-flex items-center cursor-pointer select-none hover:text-primary-700 transition-colors"
                        onClick={() => handleSort('email')}
                      >
                        Email <SortIcon field="email" />
                      </span>
                    </TableCell>
                    <TableCell header>
                      <span
                        className="inline-flex items-center cursor-pointer select-none hover:text-primary-700 transition-colors"
                        onClick={() => handleSort('role')}
                      >
                        Role <SortIcon field="role" />
                      </span>
                    </TableCell>
                    <TableCell header>Phone</TableCell>
                    <TableCell header>
                      <span
                        className="inline-flex items-center cursor-pointer select-none hover:text-primary-700 transition-colors"
                        onClick={() => handleSort('isActive')}
                      >
                        Status <SortIcon field="isActive" />
                      </span>
                    </TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow key={user._id}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user._id)}
                          onChange={() => handleSelectUser(user._id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 cursor-pointer"
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-500 text-sm">
                          {(pagination.page - 1) * pagination.limit + index + 1}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-600 font-medium">
                              {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {user.firstName} {user.lastName}
                            </p>
                            {user.department && (
                              <p className="text-xs text-gray-500 truncate">{user.department}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <span className="text-gray-600 truncate block">{user.email}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'admin' ? 'primary' : user.role === 'manager' ? 'info' : 'default'} size="sm">
                          {formatLabel(user.role)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-gray-600">
                            {getDisplayPhone(user)}
                          </span>
                          {getPhonesCount(user) > 1 && (
                            <span className="text-xs text-gray-400">
                              +{getPhonesCount(user) - 1} more
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.isActive !== false ? 'success' : 'danger'} size="sm">
                          {user.isActive !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Tooltip text="View User">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<HiOutlineEye className="w-4 h-4" />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/app/users/${user._id}`);
                              }}
                            />
                          </Tooltip>
                          <Tooltip text="Edit User">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<HiOutlinePencil className="w-4 h-4" />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditUser(user);
                              }}
                            />
                          </Tooltip>
                          <Tooltip text="Delete User">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(user);
                              }}
                            />
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {users.map((user, index) => (
                <div
                  key={user._id}
                  className="p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                >
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-gray-400 font-medium w-5 text-center flex-shrink-0">
                        {(pagination.page - 1) * pagination.limit + index + 1}
                      </span>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => handleSelectUser(user._id)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 mt-0.5 cursor-pointer flex-shrink-0"
                      />
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-600 font-medium text-sm">
                          {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">{user.email}</p>
                        {user.department && (
                          <p className="text-xs text-gray-400 truncate">{user.department}</p>
                        )}
                      </div>
                    </div>
                    <Badge variant={getRoleBadgeVariant(user.role)} size="sm">
                      {formatLabel(user.role)}
                    </Badge>
                  </div>

                  {/* Info Row */}
                  <div className="flex items-center justify-between text-xs sm:text-sm mb-3 pl-11 sm:pl-12">
                    <div className="flex items-center gap-3 text-gray-500">
                      {(user.phones || []).length > 0 && (
                        <div className="flex items-center gap-1">
                          <HiOutlinePhone className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          <span className="truncate">{getDisplayPhone(user)}</span>
                          {getPhonesCount(user) > 1 && (
                            <span className="text-xs text-gray-400">+{getPhonesCount(user) - 1}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <Badge variant={user.isActive !== false ? 'success' : 'danger'} size="sm">
                      {user.isActive !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  {/* Actions Row */}
                  <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-100">
                    <Tooltip text="View User">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={<HiOutlineEye className="w-4 h-4 text-gray-500" />}
                        onClick={() => navigate(`/app/users/${user._id}`)}
                      />
                    </Tooltip>
                    <Tooltip text="Edit User">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={<HiOutlinePencil className="w-4 h-4 text-gray-500" />}
                        onClick={() => handleEditUser(user)}
                      />
                    </Tooltip>
                    <Tooltip text="Delete User">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                        onClick={() => handleDeleteClick(user)}
                      />
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
              <span className="text-xs sm:text-sm text-gray-500">
                {pagination.total > 0 ? (
                  <>Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users</>
                ) : (
                  'No users'
                )}
              </span>
              {pagination.totalPages > 1 && (
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </Button>

                  {buildPageList(pagination.page, pagination.totalPages).map(
                    (item, idx) =>
                      item === '...' ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 text-sm text-gray-400 select-none"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setPage(item)}
                          className={
                            page === item
                              ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                              : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          }
                        >
                          {item}
                        </button>
                      )
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* User Modal */}
        <UserModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSuccess={() => {
            queryClient.invalidateQueries(['users']);
            // Telecaller pickers elsewhere cache their own list — refresh them too
            queryClient.invalidateQueries(['telecallers']);
            queryClient.invalidateQueries(['territoryAssignableUsers']);
            refreshOrg();
          }}
        />

        {/* Delete Confirmation Modal — with data-transfer step */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete User"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-gray-600 text-sm">
              Are you sure you want to delete <span className="font-semibold">{userToDelete?.firstName} {userToDelete?.lastName}</span>? This action cannot be undone.
            </p>

            {/* Transfer step: shown while the summary loads / when the user
                still owns data. Deletion is blocked until a target is picked. */}
            {deleteSummaryQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Checking user data…
              </div>
            ) : hasDataToDelete ? (
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800 font-medium mb-2">
                    This user still owns data. Transfer it to another user before deleting.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(deleteCounts)
                      .filter(([, count]) => count > 0)
                      .map(([key, count]) => (
                        <span
                          key={key}
                          className="text-xs bg-white border border-amber-200 text-amber-700 rounded-full px-2 py-0.5"
                        >
                          {count} {key === 'callLogs' ? 'call logs' : key === 'followUps' ? 'follow-ups' : key === 'callAnalyses' ? 'call analyses' : key}
                        </span>
                      ))}
                  </div>
                </div>
                {deleteCandidates.length > 0 ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Transfer data to
                    </label>
                    <Select
                      value={transferToUserId}
                      onChange={(e) => setTransferToUserId(e.target.value)}
                      placeholder="Select a user…"
                      options={deleteCandidates.map((u) => ({
                        value: u._id,
                        label: `${u.firstName} ${u.lastName}${u.role ? ` (${u.role})` : ''}`,
                      }))}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-red-600">
                    No other active users in your organization. Create a user first, then delete this one.
                  </p>
                )}
              </div>
            ) : null}

            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleConfirmDelete}
                loading={deleteMutation.isPending}
                disabled={
                  hasDataToDelete &&
                  (!transferToUserId || deleteCandidates.length === 0)
                }
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>

        {/* Bulk Status Modal */}
        <Modal
          isOpen={showBulkStatusModal}
          onClose={() => setShowBulkStatusModal(false)}
          title="Activate Users"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-gray-600 text-sm">
              Activate <span className="font-semibold">{selectedUsers.length}</span> selected users?
            </p>
            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowBulkStatusModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => bulkUpdateStatusMutation.mutate({ ids: selectedUsers, isActive: true })}
                loading={bulkUpdateStatusMutation.isPending}
              >
                Activate
              </Button>
            </div>
          </div>
        </Modal>

        {/* Assign Target Modal */}
        <Modal
          isOpen={showAssignTargetModal}
          onClose={() => setShowAssignTargetModal(false)}
          title="Assign Daily Call Target"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              Set a daily call target for <span className="font-semibold">{selectedUsers.filter(id => users.find(u => u._id === id && u.role === 'telecaller')).length}</span> selected telecaller(s). This target will be applied to all selected telecallers.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Daily Call Target
              </label>
              <input
                type="number"
                min="0"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder="e.g., 50"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
              <p className="text-xs text-gray-400 mt-1">
                Enter 0 to remove the target from selected telecallers.
              </p>
            </div>
            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowAssignTargetModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={async () => {
                  const target = parseInt(targetValue, 10);
                  if (isNaN(target) || target < 0) {
                    toast.error('Please enter a valid number (0 or greater)');
                    return;
                  }
                  // Filter to only telecallers
                  const telecallerIds = selectedUsers.filter(id =>
                    users.find(u => u._id === id && u.role === 'telecaller')
                  );
                  if (telecallerIds.length === 0) {
                    toast.error('No telecallers selected');
                    return;
                  }
                  setAssignTargetLoading(true);
                  try {
                    const targets = telecallerIds.map(id => ({ userId: id, dailyCallTarget: target }));
                    await callTargetAPI.bulkSetTargets(targets);
                    toast.success(`Daily call target of ${target} applied to ${telecallerIds.length} telecaller(s)`);
                    setShowAssignTargetModal(false);
                    setTargetValue('');
                    setSelectedUsers([]);
                    queryClient.invalidateQueries(['users']);
                  } catch (error) {
                    toast.error(error.response?.data?.message || 'Failed to assign target');
                  } finally {
                    setAssignTargetLoading(false);
                  }
                }}
                loading={assignTargetLoading}
                disabled={!targetValue && targetValue !== '0'}
              >
                Assign Target
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default UsersList;